import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import api from '../services/api';

// Import sub-components
import PersonalInfoSection from './employee-form/PersonalInfoSection';
import EmploymentInfoSection from './employee-form/EmploymentInfoSection';
import ExitInfoSection from './employee-form/ExitInfoSection';
import WarningLettersSection from './employee-form/WarningLettersSection';
import VisaInfoSection from './employee-form/VisaInfoSection';
import FlightTicketSection from './employee-form/FlightTicketSection';
import SalarySection from './employee-form/SalarySection';
import PayrollCompanySection from './employee-form/PayrollCompanySection';
import DocumentsSection from './employee-form/DocumentsSection';
import BankDetailsSection from './employee-form/BankDetailsSection';
import PhotoUploadSection from './employee-form/PhotoUploadSection';
import EmployeeCostsSection from './employee-form/EmployeeCostsSection';
import DependentsSection from './employee-form/DependentsSection';
import AssetsSection from './employee-form/AssetsSection';
import InsuranceUploadSection from './employees/InsuranceUploadSection';
import TrackingExclusionsSection from './employee-form/TrackingExclusionsSection';
import AdvanceRecoveryDialog from './employees/AdvanceRecoveryDialog';

// Predefined departments
const PREDEFINED_DEPARTMENTS = [
  'FOH',
  'BAR',
  'KITCHEN',
  'FINANCE',
  'HUMAN RESOURCE',
  'IT',
  'OPERATIONS',
  'MARKETING',
  'ADMIN',
];

export const PREDEFINED_DESIGNATIONS = [
  'HR MANAGER',
  'FINANCE MANAGER',
  'IT MANAGER',
  'EXECUTIVE HEAD CHEF',
  'ASST FLOOR MANAGER',
  'ACCOUNTS ASSISTANT',
  'CHEF DE PARTIE',
  'BARTENDER',
  'WAITER',
  'HOSTESS',
  'CASHIER',
  'SOUS CHEF',
  'COMMIS',
  'STEWARD',
  'SECURITY',
  'DRIVER',
];

const EmployeeForm = ({ employee, onSave, onCancel }) => {
  const currentYear = new Date().getFullYear();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';
  
  // Department management state
  const [departments, setDepartments] = useState(PREDEFINED_DEPARTMENTS);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');
  
  // Designation management state
  const [designations, setDesignations] = useState(PREDEFINED_DESIGNATIONS);
  const [showAddDesignation, setShowAddDesignation] = useState(false);
  const [newDesignation, setNewDesignation] = useState('');
  
  // Ticket and warning form state
  const [newTicket, setNewTicket] = useState({ year: currentYear, month: 1, notes: '' });
  const [newWarning, setNewWarning] = useState({ issue_date: '', reason: '', severity: 'written', issued_by: '' });
  
  // Photo state
  const [photoUrl, setPhotoUrl] = useState(employee?.photo_url || null);
  
  // All employees state (for managers dropdown)
  const [allEmployees, setAllEmployees] = useState([]);
  const [hiddenPositions, setHiddenPositions] = useState([]);
  const [hiddenDepartments, setHiddenDepartments] = useState([]);
  
  // Main form data state
  const [formData, setFormData] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    whatsapp_no: employee?.whatsapp_no || '',
    date_of_birth: employee?.date_of_birth || '',
    nationality: employee?.nationality || '',
    gender: employee?.gender || 'Male',
    personal_id: employee?.personal_id || '',
    religion: employee?.religion || '',
    faith: employee?.faith || '',
    visa_designation: employee?.visa_designation || '',
    join_date: employee?.join_date || '',
    position: employee?.position || '',
    department: employee?.department || '',
    employee_role: employee?.employee_role || 'Staff',
    reporting_manager_ids: employee?.reporting_manager_ids || [],
    employment_status: employee?.employment_status || 'Active',
    visa_company: employee?.visa_company || '',
    iloe_certificate_no: employee?.iloe_certificate_no || '',
    iloe_expiry_date: employee?.iloe_expiry_date || '',
    id_documents: employee?.id_documents || {},
    payment_method: employee?.payment_method || employee?.visa_company || '',
    lop_days: employee?.lop_days || 0,
    resignation_date: employee?.resignation_date || '',
    termination_date: employee?.termination_date || '',
    last_working_date: employee?.last_working_date || '',
    _lwd_manual: (() => {
      // If employee has a last_working_date, check if it matches auto-calc from source date
      if (employee?.last_working_date) {
        const src = employee?.employment_status === 'Resigned' ? employee?.resignation_date : employee?.termination_date;
        if (src) {
          const d = new Date(src);
          d.setDate(d.getDate() + 30);
          const auto = d.toISOString().split('T')[0];
          return auto !== employee.last_working_date;
        }
      }
      return false;
    })(),
    exit_reason: employee?.exit_reason || '',
    next_of_kin_payment: employee?.next_of_kin_payment || null,
    visa_freeze_until: employee?.visa_freeze_until || null,
    visa_cancelled_date: employee?.visa_cancelled_date || null,
    visa_cancellation_reason: employee?.visa_cancellation_reason || '',
    reinstatement_date: employee?.reinstatement_date || null,
    employment_gaps: employee?.employment_gaps || [],
    warning_letters: employee?.warning_letters || [],
    salary: {
      basic: employee?.salary?.basic || 0,
      housing: employee?.salary?.housing || 0,
      transportation: employee?.salary?.transportation || 0,
      other: employee?.salary?.other || 0,
    },
    labour_contract_salary: employee?.labour_contract_salary ?? '',
    flight_ticket: {
      ticket_frequency: employee?.flight_ticket?.ticket_frequency || '2_years',
      tickets_received: employee?.flight_ticket?.tickets_received || [],
      provided_by_company: employee?.flight_ticket?.provided_by_company || false,
    },
    documents: {
      passport_number: employee?.documents?.passport_number || '',
      passport_expiry: employee?.documents?.passport_expiry || '',
      visa_number: employee?.documents?.visa_number || '',
      visa_expiry: employee?.documents?.visa_expiry || '',
      emirates_id: employee?.documents?.emirates_id || '',
      emirates_id_expiry: employee?.documents?.emirates_id_expiry || '',
      labour_number: employee?.documents?.labour_number || '',
      labour_expiry: employee?.documents?.labour_expiry || '',
    },
    bank_details: {
      bank_name: employee?.bank_details?.bank_name || '',
      account_number: employee?.bank_details?.account_number || '',
      iban: employee?.bank_details?.iban || '',
    },
    employee_costs: {
      visa_cost: employee?.employee_costs?.visa_cost || 0,
      insurance_cost: employee?.employee_costs?.insurance_cost || 0,
      uniform_cost: employee?.employee_costs?.uniform_cost || 0,
      accommodation_cost: employee?.employee_costs?.accommodation_cost || 0,
      ticket_budget: employee?.employee_costs?.ticket_budget || 0,
    },
    dependents: employee?.dependents || [],
    assets: employee?.assets || [],
    exclude_clock: employee?.exclude_clock || false,
    exclude_attendance: employee?.exclude_attendance || false,
    exclude_leave_salary: employee?.exclude_leave_salary || false,
  });

  // Fetch existing departments and designations on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [response, hiddenRes, posRes] = await Promise.all([
          api.get('/api/employees'),
          api.get('/api/employees/settings/hidden'),
          api.get('/api/positions'),
        ]);
        const employees = response.data;
        setAllEmployees(employees);
        
        const hidden = hiddenRes.data;
        setHiddenPositions(hidden.hidden_positions || []);
        setHiddenDepartments(hidden.hidden_departments || []);
        
        // Departments
        const existingDepts = [...new Set(employees.map(emp => emp.department).filter(Boolean).map(d => d.toUpperCase()))];
        const normalizedPredefined = PREDEFINED_DEPARTMENTS.map(d => d.toUpperCase());
        const customDepts = existingDepts.filter(d => !normalizedPredefined.includes(d));
        const allDepts = [...PREDEFINED_DEPARTMENTS, ...customDepts];
        setDepartments(allDepts.filter(d => !(hidden.hidden_departments || []).includes(d)));
        
        // Designations — shared list across Offer Letters, Employees and Onboarding
        setDesignations(posRes.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Form submission handler
  const [recoveryInfo, setRecoveryInfo] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);

  const finalizeSave = (data) => {
    delete data._lwd_manual;
    onSave(data);
  };

  const handleRecoveryConfirm = async () => {
    setShowRecovery(false);
    try {
      if (employee?.id && recoveryInfo?.last_working_date) {
        await api.post(`/api/leaves/advance-recovery/${employee.id}/acknowledge`, {
          last_working_date: recoveryInfo.last_working_date,
          acknowledged_by: currentUser.username || currentUser.name || 'HR',
        });
      }
    } catch (err) {
      console.error('Failed to log advance recovery', err);
    }
    if (pendingSaveData) finalizeSave(pendingSaveData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanedData = { ...formData };
    
    // Clean up empty document dates
    if (cleanedData.documents) {
      if (cleanedData.documents.passport_expiry === '') cleanedData.documents.passport_expiry = null;
      if (cleanedData.documents.visa_expiry === '') cleanedData.documents.visa_expiry = null;
      if (cleanedData.documents.emirates_id_expiry === '') cleanedData.documents.emirates_id_expiry = null;
      if (cleanedData.documents.labour_expiry === '') cleanedData.documents.labour_expiry = null;
    }
    
    // Clean up empty exit dates
    if (cleanedData.resignation_date === '') cleanedData.resignation_date = null;
    if (cleanedData.termination_date === '') cleanedData.termination_date = null;
    if (cleanedData.last_working_date === '') cleanedData.last_working_date = null;
    
    // Clean up warning letters - remove any with empty dates
    if (cleanedData.warning_letters) {
      cleanedData.warning_letters = cleanedData.warning_letters.filter(w => w.issue_date && w.issue_date !== '');
    }
    
    // Labour-contract salary: empty string → null, otherwise numeric
    if (cleanedData.labour_contract_salary === '' || cleanedData.labour_contract_salary == null) {
      cleanedData.labour_contract_salary = null;
    } else {
      cleanedData.labour_contract_salary = parseFloat(cleanedData.labour_contract_salary) || 0;
    }

    // Early-exit advance leave-salary recovery warning: when resigning/terminating
    // an existing employee before year end, check if any annual leave was paid in
    // advance beyond what they've earned by their last working day.
    const isExiting = ['Resigned', 'Terminated'].includes(cleanedData.employment_status);
    if (employee?.id && isExiting && cleanedData.last_working_date) {
      try {
        const res = await api.get(`/api/leaves/advance-recovery/${employee.id}`, {
          params: { last_working_date: cleanedData.last_working_date },
        });
        if (res.data?.applicable && res.data.recovery_days > 0) {
          setRecoveryInfo(res.data);
          setPendingSaveData(cleanedData);
          setShowRecovery(true);
          return; // Wait for admin to acknowledge before saving
        }
      } catch (err) {
        // Non-blocking — proceed with save if the check fails
        console.error('Advance recovery check failed', err);
      }
    }

    finalizeSave(cleanedData);
  };

  // Field change handlers
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleHidePosition = async (pos) => {
    try {
      await api.post('/api/employees/settings/hide-position', { position: pos });
      setDesignations(prev => prev.filter(p => p !== pos));
    } catch { /* ignore */ }
  };

  const handleHideDepartment = async (dept) => {
    try {
      await api.post('/api/employees/settings/hide-department', { department: dept });
      setDepartments(prev => prev.filter(d => d !== dept));
    } catch { /* ignore */ }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="employee-form">
      {/* Photo Upload Section at the top */}
      <div className="flex justify-center pb-4 border-b">
        <PhotoUploadSection
          employeeId={employee?.id}
          currentPhotoUrl={photoUrl}
          onPhotoChange={setPhotoUrl}
          isNew={!employee?.id}
        />
      </div>

      <PersonalInfoSection 
        formData={formData} 
        handleChange={handleChange} 
      />

      <EmploymentInfoSection 
        formData={formData}
        handleChange={handleChange}
        departments={departments}
        showAddDepartment={showAddDepartment}
        setShowAddDepartment={setShowAddDepartment}
        newDepartment={newDepartment}
        setNewDepartment={setNewDepartment}
        setDepartments={setDepartments}
        designations={designations}
        showAddDesignation={showAddDesignation}
        setShowAddDesignation={setShowAddDesignation}
        newDesignation={newDesignation}
        setNewDesignation={setNewDesignation}
        setDesignations={setDesignations}
        allEmployees={allEmployees}
        currentEmployeeId={employee?.id}
        onHidePosition={handleHidePosition}
        onHideDepartment={handleHideDepartment}
      />

      <ExitInfoSection 
        formData={formData} 
        handleChange={handleChange} 
      />

      {Array.isArray(formData.employment_gaps) && formData.employment_gaps.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="reinstatement-history">
          <h3 className="text-sm font-semibold text-green-800 mb-2">Reinstatement History</h3>
          <p className="text-[11px] text-green-700 mb-2">Past exit → rehire breaks. These day ranges are excluded from working days and length of service.</p>
          <div className="space-y-1.5">
            {formData.employment_gaps.map((g, i) => {
              const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
              const days = (g.from && g.to) ? Math.round((new Date(g.to) - new Date(g.from)) / 86400000) + 1 : null;
              return (
                <div key={i} className="text-xs text-green-900 bg-white rounded p-2 border border-green-100 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`reinstatement-gap-${i}`}>
                  <span>Left: <strong>{fmt(g.last_working_date || g.from)}</strong></span>
                  <span>Rehired: <strong>{fmt(g.reinstated_on || g.to)}</strong></span>
                  {days != null && <span className="text-green-600">({days} day{days === 1 ? '' : 's'} not counted)</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <WarningLettersSection 
        formData={formData}
        setFormData={setFormData}
        newWarning={newWarning}
        setNewWarning={setNewWarning}
        employee={employee}
      />

      <VisaInfoSection 
        formData={formData} 
        handleChange={handleChange} 
        employeeId={employee?.id}
      />

      <FlightTicketSection 
        formData={formData}
        setFormData={setFormData}
        handleNestedChange={handleNestedChange}
        newTicket={newTicket}
        setNewTicket={setNewTicket}
        employeeId={employee?.id}
      />

      <SalarySection 
        formData={formData} 
        handleNestedChange={handleNestedChange} 
        handleChange={handleChange}
      />

      <EmployeeCostsSection
        formData={formData}
        setFormData={setFormData}
      />

      <PayrollCompanySection 
        formData={formData} 
        handleChange={handleChange} 
      />

      <DocumentsSection 
        formData={formData} 
        handleNestedChange={handleNestedChange} 
      />

      <BankDetailsSection 
        formData={formData} 
        handleNestedChange={handleNestedChange} 
      />

      <DependentsSection
        formData={formData}
        setFormData={setFormData}
      />

      <AssetsSection
        formData={formData}
        setFormData={setFormData}
        employeeId={employee?.id}
      />

      {employee?.id && (
        <InsuranceUploadSection employee={employee} />
      )}

      {isSuperAdmin && (
        <TrackingExclusionsSection
          formData={formData}
          handleChange={handleChange}
        />
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-employee-btn">
          Cancel
        </Button>
        <Button type="submit" data-testid="save-employee-btn">
          Save Employee
        </Button>
      </div>

      <AdvanceRecoveryDialog
        open={showRecovery}
        onOpenChange={setShowRecovery}
        info={recoveryInfo}
        onConfirm={handleRecoveryConfirm}
      />
    </form>
  );
};

export default EmployeeForm;
